import { z } from 'zod';
import { IdSchema, DateTimeSchema, PaginationParamsSchema, HttpOrInternalFileUrlSchema, } from '../../common';
export const WorksheetStatusSchema = z.enum(['draft', 'published', 'archived']);
export const WorksheetSubmissionStatusSchema = z.enum(['in_progress', 'submitted', 'graded']);
// PDF URL — http(s):// veya data:application/pdf;base64,...
const PdfOrDataUrl = z
    .string()
    .min(1)
    .max(20_000_000)
    .refine((v) => v.startsWith('data:application/pdf') || HttpOrInternalFileUrlSchema.safeParse(v).success, 'pdfUrl must be a https URL, internal file path or data:application/pdf URL');
// Cevap key satırı: 'A'..'E' veya boş (atlanmış).
const AnswerKeyEntry = z
    .string()
    .regex(/^[A-Ea-e]?$/, 'A-E veya boş olmalı')
    .default('');
export const WorksheetScoringModeSchema = z.enum(['none', 'neg3_to_1', 'neg4_to_1']);
export const WorksheetSchema = z.object({
    id: IdSchema,
    title: z.string(),
    description: z.string().nullable(),
    pdfUrl: z.string(),
    questionCount: z.number().int(),
    answerKey: z.array(z.string()),
    choiceCount: z.number().int().min(2).max(5),
    status: WorksheetStatusSchema,
    subjectId: IdSchema.nullable(),
    unitId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    gradeLevel: z.number().int().min(1).max(12).nullable(),
    courseId: IdSchema.nullable(),
    // WP eklentisi parite alanlari
    durationMin: z.number().int().nullable(),
    startsAt: DateTimeSchema.nullable(),
    endsAt: DateTimeSchema.nullable(),
    resultsAt: DateTimeSchema.nullable(),
    scoringMode: WorksheetScoringModeSchema,
    showResultsImmediately: z.boolean(),
    maxAttempts: z.number().int(),
    coefficient: z.number(),
    hidePdf: z.boolean(),
    createdById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    subjectName: z.string().nullable().optional(),
    topicName: z.string().nullable().optional(),
    createdByName: z.string().nullable().optional(),
    submissionCount: z.number().int().optional(),
});
export const CreateWorksheetSchema = z.object({
    title: z.string().min(1).max(200),
    description: z.string().max(2000).optional(),
    pdfUrl: PdfOrDataUrl,
    questionCount: z.coerce.number().int().min(1).max(200),
    answerKey: z.array(AnswerKeyEntry).min(1).max(200),
    choiceCount: z.coerce.number().int().min(2).max(5).optional(),
    subjectId: IdSchema.optional(),
    unitId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    courseId: IdSchema.optional(),
    status: WorksheetStatusSchema.optional(),
    // WP eklentisi parite alanlari (hepsi optional, backend default'lari korur)
    durationMin: z.coerce.number().int().min(0).max(600).optional(),
    startsAt: z.string().datetime().optional(),
    endsAt: z.string().datetime().optional(),
    resultsAt: z.string().datetime().optional(),
    scoringMode: WorksheetScoringModeSchema.optional(),
    showResultsImmediately: z.boolean().optional(),
    maxAttempts: z.coerce.number().int().min(1).max(20).optional(),
    coefficient: z.coerce.number().min(0).max(10).optional(),
    hidePdf: z.boolean().optional(),
});
export const UpdateWorksheetSchema = CreateWorksheetSchema.partial();
export const WorksheetQuerySchema = PaginationParamsSchema.extend({
    search: z.string().optional(),
    subjectId: IdSchema.optional(),
    unitId: IdSchema.optional(),
    topicId: IdSchema.optional(),
    gradeLevel: z.coerce.number().int().min(1).max(12).optional(),
    status: WorksheetStatusSchema.optional(),
    courseId: IdSchema.optional(),
});
// --- Submission ---
export const WorksheetSubmissionSchema = z.object({
    id: IdSchema,
    worksheetId: IdSchema,
    studentUserId: IdSchema,
    status: WorksheetSubmissionStatusSchema,
    answers: z.array(z.string()),
    correctCount: z.number().int().nullable(),
    wrongCount: z.number().int().nullable(),
    blankCount: z.number().int().nullable(),
    durationSec: z.number().int().nullable(),
    startedAt: DateTimeSchema,
    submittedAt: DateTimeSchema.nullable(),
    studentName: z.string().nullable().optional(),
});
export const SubmitWorksheetSchema = z.object({
    answers: z.array(AnswerKeyEntry).min(1).max(200),
    durationSec: z.coerce.number().int().nonnegative().optional(),
});
// --- Worksheet Items (per-question metadata) ---
export const WorksheetItemSchema = z.object({
    id: IdSchema,
    worksheetId: IdSchema,
    questionNumber: z.number().int().min(1).max(200),
    subjectId: IdSchema.nullable(),
    unitId: IdSchema.nullable(),
    topicId: IdSchema.nullable(),
    acquisition: z.string().nullable(),
    createdAt: DateTimeSchema,
    updatedAt: DateTimeSchema,
    // hydrated names (optional, server fills for UI)
    subjectName: z.string().nullable().optional(),
    unitName: z.string().nullable().optional(),
    topicName: z.string().nullable().optional(),
});
// Tek soru icin patch (bulk upsert)
export const WorksheetItemInputSchema = z.object({
    questionNumber: z.number().int().min(1).max(200),
    subjectId: IdSchema.nullable().optional(),
    unitId: IdSchema.nullable().optional(),
    topicId: IdSchema.nullable().optional(),
    acquisition: z.string().max(500).nullable().optional(),
});
// PUT /worksheets/:id/items — gelen liste ile mevcudu replace eder
export const BulkUpsertWorksheetItemsSchema = z.object({
    items: z.array(WorksheetItemInputSchema).min(1).max(200),
});
// POST /worksheets/:id/items/apply-all — toplu uygulama (tum sorulara ayni metadata)
export const ApplyAllWorksheetItemsSchema = z.object({
    subjectId: IdSchema.nullable().optional(),
    unitId: IdSchema.nullable().optional(),
    topicId: IdSchema.nullable().optional(),
    acquisition: z.string().max(500).nullable().optional(),
});
// --- Worksheet Assignment ---
export const WorksheetAssignmentTargetSchema = z.enum(['course', 'camp', 'student']);
export const WorksheetAssignmentSchema = z.object({
    id: IdSchema,
    worksheetId: IdSchema,
    target: WorksheetAssignmentTargetSchema,
    targetId: IdSchema,
    assignedById: IdSchema.nullable(),
    createdAt: DateTimeSchema,
    targetName: z.string().nullable().optional(),
});
export const CreateWorksheetAssignmentSchema = z.object({
    target: WorksheetAssignmentTargetSchema,
    targetIds: z.array(IdSchema).min(1).max(200),
});
//# sourceMappingURL=worksheet.js.map